import { gql, useQuery } from '@apollo/client';
import IconErrorSolid from '@apollo/icons/default/IconErrorSolid.svg';
import { Badge, Spinner } from '@apollo/orbit';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import Config from 'src/lib/config';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

export const ContractBadgeServiceFragment = gql`
  fragment ContractBadgeServiceFragment on Service {
    id
    variant(name: $graphVariant) {
      id
      latestLaunch {
        id
        build {
          result {
            ... on BuildFailure {
              errorMessages {
                code
                message
                locations {
                  column
                  line
                }
              }
            }
            ... on BuildSuccess {
              coreSchema {
                coreHash
              }
            }
          }
        }
      }
    }
  }
`;

const ContractBadgeStatus = gql`
  query ContractBadgeStatusQuery($graphId: ID!, $graphVariant: String!) {
    service(id: $graphId) {
      id
      ...ContractBadgeServiceFragment
    }
  }
  ${ContractBadgeServiceFragment}
`;

/**
 * To fetch the build status for a contract (ie. Launch in progress, Build Errors)
 * the graphId and graphVariant props will need to be provided to satisfy the
 * polling query.
 */
interface Props {
  graphId: string;
  graphVariant: string;
}

export const ContractBadge = ({ graphId, graphVariant }: Props) => {
  const location = useLocation();

  const { data, loading } = useQuery<
    GraphQLTypes.ContractBadgeStatusQuery,
    GraphQLTypes.ContractBadgeStatusQueryVariables
  >(ContractBadgeStatus, {
    variables: {
      graphId,
      graphVariant,
    },
    pollInterval: 30000,
  });

  const buildResult = data?.service?.variant?.latestLaunch?.build?.result;
  const launchInProgress = buildResult === undefined;

  let buildErrors:
    | GraphQLTypes.ContractBadgeServiceFragment_variant_latestLaunch_build_result_BuildFailure_errorMessages[]
    | undefined;
  if (buildResult && 'errorMessages' in buildResult) {
    buildErrors = buildResult.errorMessages;
  }

  if (loading) {
    return null;
  }

  return launchInProgress ? (
    <Tooltip placement="bottom" label="Launch in progress">
      <Badge icon={<Spinner size="tiny" />}>Contract</Badge>
    </Tooltip>
  ) : buildErrors ? (
    <Tooltip
      placement="bottom"
      label={`Click to see ${buildErrors?.length} build ${format.pluralize(
        buildErrors?.length,
        'error',
      )} stopping your contract from building.`}
    >
      <Link
        to={setOverlayVisibleLocation({
          location,
          overlayName: Config.modals.launchErrors,
        })}
      >
        <Badge variant="error" icon={<IconErrorSolid />}>
          Contract
        </Badge>
      </Link>
    </Tooltip>
  ) : (
    <Tooltip
      placement="bottom"
      label="Click to see details about this contract."
    >
      <Link
        to={setOverlayVisibleLocation({
          location,
          overlayName: Config.modals.viewContractDetails,
          additionalSearchParameters: {
            [Config.queryParameters.ContractVariant]: graphVariant,
          },
        })}
      >
        <Badge>Contract</Badge>
      </Link>
    </Tooltip>
  );
};
